import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { sendFileDownloadRequests } from "~/classes/handlers/file/download"

// TODO: what if the file is too large

interface State {
  file: InterfaceFile
  loading: boolean
  errored: boolean
  isCached: boolean
  presignedUrl?: string
  objectUrl?: string
}

const console = useLogger("use-renderer", theme.colors.purple.hex)

export default function ({ file }: { file: Ref<InterfaceFile> }) {
  const rendererState: Ref<State> = ref({
    file: file.value,
    loading: true,
    errored: false,
    isCached: false,
  })
  const { isOffline } = useCache()
  const { sandboxPathAsWebFriendlyUri, getFileOfflineStatus, downloadFileToSandbox } = useSandbox()
  const { addInterfaceEventListener, removeInterfaceEventListenerByRequestId } = useEvents()

  async function getObjectUrl() {
    const sandboxPath = await getFileOfflineStatus(rendererState.value.file)
    if (sandboxPath) {
      await setObjectUrl(sandboxPath)
      rendererState.value.isCached = true
      rendererState.value.loading = false
      rendererState.value.errored = false
      return
    }

    const requestId = generateWorkerRequestId()
    addInterfaceEventListener("fileDownloadResponse", onDownloadPrepared, { requestId })
    const fileId = rendererState.value.file.fileId
    sendFileDownloadRequests([{
      requestId,
      file: InterfaceFile.blank(fileId)._file,
      offline: isOffline(),
    }])
  }

  async function setObjectUrl(sandboxPath: string) {
    const url = await sandboxPathAsWebFriendlyUri(sandboxPath)
    rendererState.value.objectUrl = url
  }

  async function onDownloadPrepared(response: FileDownloadResponseDatagram) {
    removeInterfaceEventListenerByRequestId("fileDownloadResponse", response.requestId)
    console.debug("download response", response)
    if (response.error || !response.file) {
      rendererState.value.errored = true
      console.error("cannot preview/peek image", response)
      return
    }
    rendererState.value.file.mergeWithNewer(new InterfaceFile(response.file))

    if (response.presignedUri) {
      rendererState.value.presignedUrl = response.presignedUri
      const checksum = rendererState.value.file.checksum()
      if (!checksum)
        throw new Error("No checksum in file, cannot download to cache")
      const sandboxPath = await downloadFileToSandbox(checksum, response.presignedUri)
      await setObjectUrl(sandboxPath)
      rendererState.value.loading = false
      rendererState.value.errored = false
      rendererState.value.isCached = true
      return
    }

    // if there's no presigned uri, the image is already cached
    const sandboxPath = await getFileOfflineStatus(rendererState.value.file)
    if (!sandboxPath)
      throw new Error("Received cached response but found no sandbox url")
    await setObjectUrl(sandboxPath)
    rendererState.value.loading = false
    rendererState.value.errored = false
    rendererState.value.isCached = true
    return
  }

  /**
   * TODO: we can also take other actions here, like potentially updating the file or
   * notifying the user that the file has been changed
   */
  function onFileUpdated(newFile: InterfaceFile, _oldFile?: InterfaceFile) {
    rendererState.value.file = newFile
    return
  }

  onMounted(async () => {
    getObjectUrl()
  })

  onUnmounted(() => {
    if (!isDesktop() && rendererState.value.objectUrl)
      URL.revokeObjectURL(rendererState.value.objectUrl)
  })

  watch(file, onFileUpdated)

  return {
    rendererState,
  }
}
